// Backend v2 report document -> the v95 report engine's data shape.
//
// Display data only: names, IDs, dates, the values the backend selected, their
// day-level histories, remarks and FIM overrides. Grades, statuses, trends,
// tiers, thresholds and freshness are NOT copied here; the engine reads those
// straight from the document (public/mobility-report/server-report.js).
import { resolveMobilityMode } from "./mobilityMode";

const list = (value) => (Array.isArray(value) ? value : []);
const text = (value) =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";
const label = (value) =>
  value && typeof value === "object"
    ? text(value.label ?? value.display ?? value.name ?? value.display_name)
    : text(value);
const finite = (value) =>
  value !== null && value !== "" && Number.isFinite(Number(value));
const titleCase = (value) => text(value).replace(/^./, (c) => c.toUpperCase());

// Engine mode id ("Safety", "home_care", ...) for the document's own mode.
export const engineModeForReport = (doc) =>
  resolveMobilityMode({
    customer_type: doc?.context?.mode || doc?.context?.customer_type,
  });

const pointFrom = (point) => ({
  date: text(point.date).slice(0, 10),
  value: Number(point.value),
  test_type: text(point.source?.test_type ?? point.test_type),
  confidence: text(point.source?.confidence ?? point.confidence),
  report_id: point.source?.assessment_id ?? point.assessment_id ?? null,
});

export const buildEngineDataFromServerReport = (doc, { photoUrl } = {}) => {
  if (!doc || typeof doc !== "object") return null;
  const person = doc.person || {};
  const assessment = doc.assessment || {};
  const remarks = doc.remarks || {};
  const patientId = String(doc.context?.senior_id ?? "current_patient");
  const lastTest = assessment.last_test || {};
  const metrics = doc.metrics || {};

  const measurements = [];
  const history = {};
  Object.entries(metrics).forEach(([api, metric]) => {
    if (!metric || typeof metric !== "object") return;
    if (finite(metric.value)) {
      measurements.push({
        metric_api: api,
        value: Number(metric.value),
        unit: text(metric.canonical_unit ?? metric.display_unit),
        test_type: text(metric.source?.test_type),
        confidence: text(metric.source?.confidence),
        report_id: metric.source?.assessment_id ?? null,
      });
    }
    const points = list(metric.history)
      .filter((point) => point && finite(point.value) && point.date)
      .map(pointFrom);
    if (points.length) history[api] = points;
  });

  const patient = {
    patient_id: patientId,
    test_person_id: text(person.external_id) || "—",
    first_name: text(person.first_name),
    last_name: text(person.last_name),
    age: Number(person.age) || 0,
    gender: titleCase(person.gender),
    seniorlife_id: text(person.seniorlife_id) || "—",
    location: label(person.location) || "SeniorLife",
    avatar_url:
      person.photo?.url ||
      person.avatar_url ||
      person.photo_url ||
      photoUrl ||
      null,
    profile_complete: true,
  };

  const fim = doc.fim || {};
  return {
    schema_version: "v2",
    mock_data: {
      patients_list: [patient],
      patient_details: {
        [patientId]: {
          ...patient,
          past_injury:
            label(person.injury) ||
            list(person.injury?.items).map(label).filter(Boolean).join(", ") ||
            "None",
          equipment_used:
            label(assessment.equipment) || text(lastTest.test_label) || "",
          default_mode: engineModeForReport(doc),
          default_activity: text(doc.context?.activity_id) || "Grandparenting",
          last_test: {
            test_type: text(lastTest.test_type),
            date: text(lastTest.date ?? assessment.last_assessment_date),
          },
          contributing_tests: list(assessment.contributing_tests).map(
            (test) => ({
              test_type: text(test.test_type),
              date: text(test.date),
            }),
          ),
          height_cm: finite(person.height?.value_cm)
            ? Number(person.height.value_cm)
            : null,
          weight_kg: finite(person.weight?.value_kg)
            ? Number(person.weight.value_kg)
            : null,
          exercise_frequency: "",
          exercise_recommendations: [],
          exercise_program: [],
          injuries: {},
          medications: {},
          symptoms: {},
        },
      },
      measurements: {
        [patientId]: {
          patient_id: patientId,
          session: {
            record_date: text(assessment.last_assessment_date),
            tests_performed: list(assessment.contributing_tests).map((test) =>
              text(test.test_type),
            ),
            staff_remark: text(remarks.staff?.text),
            test_person_remark: text(remarks.person?.text),
          },
          measurements,
        },
      },
      history: { [patientId]: history },
      fim_overrides: {
        [patientId]: {
          report_id: doc.report_id ?? null,
          assessment_timestamp: text(assessment.last_assessment_date) || null,
          overrides: list(fim.overrides),
          history: list(fim.override_history),
        },
      },
    },
  };
};
